import React, { useCallback, useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, History, Plus, RefreshCw, ShieldCheck, Sliders, Sparkles, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
    { v: "license_status", label: "License status" },
    { v: "license_expires_days", label: "Days until license expires" },
    { v: "criminal_records", label: "# criminal records" },
    { v: "nsopw_status", label: "NSOPW status" },
    { v: "ofac_status", label: "OFAC status" },
    { v: "risk_score", label: "Composite risk score (0-100)" },
];
const OPS = [
    { v: "eq", label: "equals" }, { v: "ne", label: "not equals" },
    { v: "gt", label: "greater than" }, { v: "lt", label: "less than" },
    { v: "contains", label: "contains" },
];
const CADENCES = [
    { v: "off", label: "Manual only" },
    { v: "daily", label: "Daily" },
    { v: "weekly", label: "Weekly" },
    { v: "monthly", label: "Monthly" },
];
const bandColors = {
    green: "bg-emerald-100 text-emerald-800",
    yellow: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
};

export default function BackgroundMonitor() {
    const [sub, setSub] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [rules, setRules] = useState([]);
    const [busy, setBusy] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historySubject, setHistorySubject] = useState(null);
    const [history, setHistory] = useState([]);

    const load = useCallback(async () => {
        try {
            const s = (await api.get("/monitor/subscription")).data;
            setSub(s);
            if (s.active) {
                const [subs, rls] = await Promise.all([
                    api.get("/monitor/subjects"),
                    api.get("/monitor/rules"),
                ]);
                setSubjects(subs.data);
                setRules(rls.data);
            }
        } catch (e) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function startTrial() {
        try {
            await api.post("/monitor/subscription/start");
            toast.success("7-day free monitoring trial started — 25 subjects included.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Trial failed");
        }
    }

    async function runNow(sid) {
        setBusy(true);
        try {
            const r = await api.post(`/monitor/subjects/${sid}/run`);
            toast.success(`Check done — ${r.data.band.toUpperCase()} · risk ${r.data.risk_score}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Run failed");
        } finally { setBusy(false); }
    }

    async function deleteSubject(sid) {
        if (!window.confirm("Delete this subject and all history?")) return;
        try {
            await api.delete(`/monitor/subjects/${sid}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    }

    async function viewHistory(subj) {
        setHistorySubject(subj);
        setHistoryOpen(true);
        try {
            const r = await api.get(`/monitor/subjects/${subj.id}/runs`);
            setHistory(r.data);
        } catch (e) { setHistory([]); }
    }

    if (sub === null) {
        return (
            <div className="min-h-screen bg-stone-50">
                <TopNav />
                <main className="max-w-5xl mx-auto px-6 py-10 text-sm text-stone-500 font-dmsans">Loading monitor…</main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid="monitor-page" className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-700 mb-2 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" /> B2B add-on
                    </div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Background Monitoring
                    </h1>
                    <p className="text-stone-600 font-dmsans mt-2 max-w-2xl">
                        Save people to auto-recheck on a daily / weekly / monthly cadence. Define your own red/yellow rules so alerts fire on the fields YOU care about.
                    </p>
                </div>

                {!sub.active ? (
                    <Card className="rounded-2xl border-indigo-200 border-dashed border-2 bg-indigo-50/40" data-testid="monitor-subscribe-card">
                        <CardContent className="p-6">
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-6 h-6 text-indigo-700 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-outfit font-semibold text-lg text-stone-900">Start a 7-day free trial</div>
                                    <p className="text-sm font-dmsans text-stone-700 mt-1">
                                        For price schedule, <a href="https://careco24.com/contact" target="_blank" rel="noopener noreferrer" className="underline text-indigo-700 hover:text-indigo-900 font-medium" data-testid="monitor-pricing-contact-link">contact us at careco24.com</a>. Monitor up to 25 subjects during your trial · unlimited on-demand re-checks · define your own flag rules · cancel any time.
                                    </p>
                                    <Button onClick={startTrial} data-testid="monitor-start-trial-btn" className="rounded-full mt-4 h-11 px-6 bg-indigo-700 hover:bg-indigo-800 text-white font-outfit">
                                        Start free trial
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-3 mb-6 items-center">
                            <Badge className="bg-emerald-700 text-white rounded-full">
                                {sub.plan} · {sub.current_subjects}/{sub.included_this_month} subjects
                            </Badge>
                            <Button onClick={() => setAddOpen(true)} data-testid="monitor-add-subject-btn" className="rounded-full bg-indigo-700 hover:bg-indigo-800 text-white h-10 px-5">
                                <Plus className="w-4 h-4 mr-1" /> Add subject
                            </Button>
                            <Button onClick={() => setRulesOpen(true)} data-testid="monitor-rules-btn" variant="outline" className="rounded-full h-10 px-5 border-indigo-200 text-indigo-800 hover:bg-indigo-50">
                                <Sliders className="w-4 h-4 mr-1" /> Flag rules ({rules.length})
                            </Button>
                        </div>

                        <Card className="rounded-2xl border-stone-200 overflow-hidden">
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Subject</TableHead>
                                            <TableHead>License</TableHead>
                                            <TableHead>Cadence</TableHead>
                                            <TableHead>Last band</TableHead>
                                            <TableHead>Last checked</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {subjects.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center text-sm text-stone-500 font-dmsans py-10">
                                                    No subjects yet. Add one to start monitoring.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {subjects.map((s) => (
                                            <TableRow key={s.id} data-testid={`monitor-subject-row-${s.id}`}>
                                                <TableCell>
                                                    <div className="font-medium text-stone-900">{s.first_name} {s.last_name}</div>
                                                    <div className="text-xs text-stone-500 font-dmsans">
                                                        {s.dob || "—"} · {s.state || "—"}{s.email ? ` · ${s.email}` : ""}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm font-dmsans">
                                                    {s.license_type || "—"}{s.license_number ? ` #${s.license_number}` : ""}
                                                </TableCell>
                                                <TableCell className="text-xs uppercase tracking-wider font-semibold text-stone-600">{s.cadence}</TableCell>
                                                <TableCell>
                                                    {s.last_band
                                                        ? <Badge className={`${bandColors[s.last_band]} rounded-full`}>{s.last_band}</Badge>
                                                        : <span className="text-xs text-stone-400 font-dmsans">Never run</span>}
                                                </TableCell>
                                                <TableCell className="text-xs text-stone-500 font-dmsans">
                                                    {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "—"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button size="sm" variant="ghost" data-testid={`monitor-run-btn-${s.id}`} onClick={() => runNow(s.id)} disabled={busy} title="Run check now" className="h-8">
                                                            <RefreshCw className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" data-testid={`monitor-history-btn-${s.id}`} onClick={() => viewHistory(s)} title="View history" className="h-8">
                                                            <History className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" data-testid={`monitor-delete-btn-${s.id}`} onClick={() => deleteSubject(s.id)} title="Delete" className="h-8 text-red-600 hover:bg-red-50">
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </>
                )}
            </main>

            <AddSubjectDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
            <RulesDialog open={rulesOpen} onOpenChange={setRulesOpen} rules={rules} onChanged={load} />
            <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} subject={historySubject} runs={history} />
        </div>
    );
}


function AddSubjectDialog({ open, onOpenChange, onSaved }) {
    const [form, setForm] = useState({
        first_name: "", last_name: "", dob: "", email: "", state: "",
        license_number: "", license_type: "", cadence: "off",
    });
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    async function submit(e) {
        e.preventDefault();
        try {
            const payload = { ...form };
            Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
            await api.post("/monitor/subjects", payload);
            toast.success("Subject added — you can now run a check.");
            onOpenChange(false);
            setForm({ first_name: "", last_name: "", dob: "", email: "", state: "", license_number: "", license_type: "", cadence: "off" });
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add");
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-outfit">Add a monitored subject</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 pt-2">
                    <div><Label>First name *</Label>
                        <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="monitor-add-first" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div><Label>Last name *</Label>
                        <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="monitor-add-last" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div><Label>Date of birth</Label>
                        <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} data-testid="monitor-add-dob" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div><Label>Email</Label>
                        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="monitor-add-email" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div><Label>State</Label>
                        <Input maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))} placeholder="NY" data-testid="monitor-add-state" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div><Label>License type</Label>
                        <Select value={form.license_type || undefined} onValueChange={(v) => set("license_type", v)}>
                            <SelectTrigger data-testid="monitor-add-license-type" className="h-11 mt-1 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["RN", "LPN", "CNA", "HHA", "OTHER"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="sm:col-span-2"><Label>License number</Label>
                        <Input value={form.license_number} onChange={(e) => set("license_number", e.target.value)} data-testid="monitor-add-license-number" className="h-11 mt-1 rounded-xl" />
                    </div>
                    <div className="sm:col-span-2"><Label>Auto-recheck cadence</Label>
                        <Select value={form.cadence} onValueChange={(v) => set("cadence", v)}>
                            <SelectTrigger data-testid="monitor-add-cadence" className="h-11 mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CADENCES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter className="sm:col-span-2 mt-2">
                        <Button type="submit" data-testid="monitor-add-submit-btn" className="rounded-full bg-indigo-700 hover:bg-indigo-800 text-white">Save subject</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


function RulesDialog({ open, onOpenChange, rules, onChanged }) {
    const [form, setForm] = useState({ name: "", field: "license_status", operator: "eq", value: "", color: "red" });
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    async function submit(e) {
        e.preventDefault();
        try {
            await api.post("/monitor/rules", form);
            toast.success("Rule added");
            setForm({ name: "", field: "license_status", operator: "eq", value: "", color: "red" });
            onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    async function del(id) {
        try {
            await api.delete(`/monitor/rules/${id}`);
            toast.success("Rule removed");
            onChanged();
        } catch (err) { toast.error("Failed"); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-outfit">Custom flag rules</DialogTitle></DialogHeader>
                <p className="text-sm text-stone-600 font-dmsans">
                    Rules run against every monitored subject's check. First matched RED wins; else YELLOW; else GREEN.
                </p>
                <form onSubmit={submit} className="grid gap-3 sm:grid-cols-6 pt-3 items-end">
                    <div className="sm:col-span-2"><Label>Rule name</Label>
                        <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="License expired" required data-testid="monitor-rule-name" className="h-10 mt-1 rounded-xl" />
                    </div>
                    <div className="sm:col-span-2"><Label>Field</Label>
                        <Select value={form.field} onValueChange={(v) => set("field", v)}>
                            <SelectTrigger data-testid="monitor-rule-field" className="h-10 mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {FIELDS.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Operator</Label>
                        <Select value={form.operator} onValueChange={(v) => set("operator", v)}>
                            <SelectTrigger data-testid="monitor-rule-op" className="h-10 mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {OPS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Value</Label>
                        <Input value={form.value} onChange={(e) => set("value", e.target.value)} required data-testid="monitor-rule-value" className="h-10 mt-1 rounded-xl" />
                    </div>
                    <div className="sm:col-span-2"><Label>Flag color</Label>
                        <Select value={form.color} onValueChange={(v) => set("color", v)}>
                            <SelectTrigger data-testid="monitor-rule-color" className="h-10 mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="red">Red (block)</SelectItem>
                                <SelectItem value="yellow">Yellow (warn)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="sm:col-span-4">
                        <Button type="submit" data-testid="monitor-rule-add-btn" className="rounded-full bg-indigo-700 hover:bg-indigo-800 text-white h-10 px-5">
                            <Plus className="w-4 h-4 mr-1" /> Add rule
                        </Button>
                    </div>
                </form>

                <div className="mt-3">
                    <Table>
                        <TableHeader><TableRow>
                            <TableHead>Rule</TableHead>
                            <TableHead>Condition</TableHead>
                            <TableHead>Color</TableHead>
                            <TableHead></TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {rules.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-sm text-stone-500 py-6 font-dmsans">
                                        No rules defined. Every check will resolve to green until you add rules.
                                    </TableCell>
                                </TableRow>
                            )}
                            {rules.map((r) => {
                                const f = FIELDS.find((x) => x.v === r.field)?.label || r.field;
                                const o = OPS.find((x) => x.v === r.operator)?.label || r.operator;
                                return (
                                    <TableRow key={r.id}>
                                        <TableCell className="font-medium text-stone-900">{r.name}</TableCell>
                                        <TableCell className="text-sm font-dmsans text-stone-700">
                                            {f} {o} <span className="font-mono">{r.value}</span>
                                        </TableCell>
                                        <TableCell><Badge className={`${bandColors[r.color]} rounded-full`}>{r.color}</Badge></TableCell>
                                        <TableCell className="text-right">
                                            <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`monitor-rule-delete-${r.id}`} className="text-red-600 hover:bg-red-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function HistoryDialog({ open, onOpenChange, subject, runs }) {
    if (!subject) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-outfit">
                        Audit trail — {subject.first_name} {subject.last_name}
                    </DialogTitle>
                </DialogHeader>
                {runs.length === 0 && <p className="text-sm text-stone-500 font-dmsans py-4">No runs yet.</p>}
                <div className="space-y-4 mt-2">
                    {runs.map((r) => (
                        <div key={r.id} className="rounded-xl border border-stone-200 p-4 bg-white">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                <Badge className={`${bandColors[r.band]} rounded-full`}>{r.band}</Badge>
                                <span className="text-sm font-outfit font-semibold text-stone-900">Risk {r.risk_score}/100</span>
                                <span className="text-xs text-stone-500 font-dmsans">{new Date(r.run_at).toLocaleString()}</span>
                                <Badge className="bg-stone-100 text-stone-800 rounded-full text-[10px] uppercase">{r.triggered_by}</Badge>
                                {r.mocked && <Badge className="bg-amber-700 text-white rounded-full text-[10px] uppercase">Mocked</Badge>}
                            </div>
                            {r.triggered_rules?.length > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-stone-600 mb-1">Triggered rules</div>
                                    <ul className="text-xs space-y-1">
                                        {r.triggered_rules.map((t, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <Badge className={`${bandColors[t.color]} rounded-full text-[10px]`}>{t.color}</Badge>
                                                <span className="text-stone-800 font-medium">{t.name}</span>
                                                <span className="text-stone-500 font-mono text-[10px]">{t.field}={String(t.matched_value)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="mt-3">
                                <div className="text-xs font-semibold uppercase tracking-wider text-stone-600 mb-1">Sources checked</div>
                                <ul className="text-xs space-y-1">
                                    {(r.sources || []).map((s, i) => (
                                        <li key={i} className="flex items-center gap-2 font-dmsans">
                                            {s.status === "clear"
                                                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                                                : <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />}
                                            <span className="text-stone-900 font-medium">{s.name}</span>
                                            <span className="text-stone-500">— {s.status}{s.records_found > 0 ? ` (${s.records_found})` : ""}</span>
                                            {s.note && <span className="text-amber-700 italic">· {s.note}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
